import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoveDownIcon from '@mui/icons-material/MoveDown'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState, type ReactNode } from 'react'
import {
  useContractors,
  usePlanMutations,
  usePlanSummary,
  type LabourPlan,
  type MaterialPlan,
  type PlanPriority,
  type WorkPlan,
  type WorkPlanStatus,
} from '../../api/execution'
import { useLabourCategories, useMaterials } from '../../api/hooks'
import { ErrorBlock, LoadingBlock } from '../../components/Feedback'

const PRIORITIES: PlanPriority[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']
const WORK_STATUSES: WorkPlanStatus[] = [
  'PLANNED',
  'READY',
  'MATERIAL_PENDING',
  'LABOUR_PENDING',
  'DRAWING_PENDING',
  'POSTPONED',
]

const PRIORITY_COLOR: Record<PlanPriority, { color: string; bg: string }> = {
  CRITICAL: { color: '#8c1d18', bg: '#fbe0de' },
  HIGH: { color: '#9a4a00', bg: '#fdeecf' },
  MEDIUM: { color: '#0d4a8a', bg: '#dceaf8' },
  LOW: { color: '#4a5a68', bg: '#e6ebef' },
}

/**
 * Tomorrow's plan: material, labour and work, with the shortages the site will hit
 * if nobody acts tonight.
 */
export function PlanningTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const summary = usePlanSummary(reportId)
  const m = usePlanMutations(reportId)
  const [dialog, setDialog] = useState<'material' | 'labour' | 'work' | null>(null)
  const [editMaterial, setEditMaterial] = useState<MaterialPlan | null>(null)
  const [editLabour, setEditLabour] = useState<LabourPlan | null>(null)
  const [editWork, setEditWork] = useState<WorkPlan | null>(null)

  if (summary.isLoading) return <LoadingBlock />
  if (summary.error) return <ErrorBlock error={summary.error} />
  const plan = summary.data
  if (!plan) return null

  const a = plan.alerts
  const anyAlert = a.materialShortages + a.labourShortages + a.blockedActivities > 0

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Plan for <strong>{plan.planningDate}</strong>. Shortage is calculated, not typed.
        </Typography>
        {editable && (
          <Button
            size="small"
            startIcon={<MoveDownIcon />}
            onClick={() => m.carryForward.mutate()}
            disabled={m.carryForward.isPending}
          >
            Carry forward unfinished
          </Button>
        )}
      </Box>

      {anyAlert && (
        <Alert severity={a.criticalItems > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
          {a.materialShortages > 0 && <>{a.materialShortages} material shortage(s). </>}
          {a.labourShortages > 0 && <>{a.labourShortages} labour shortage(s). </>}
          {a.blockedActivities > 0 && <>{a.blockedActivities} activity blocked. </>}
          {a.criticalItems > 0 && <strong>{a.criticalItems} marked critical.</strong>}
        </Alert>
      )}

      <PlanSection
        title="Material required tomorrow"
        onAdd={editable ? () => setDialog('material') : undefined}
        columns={['Material', 'Unit', 'Required', 'Available', 'Shortage', 'By', 'Priority', '']}
        rows={plan.materials.map((r) => [
          `${r.materialCode ?? ''} ${r.materialName ?? ''}`.trim(),
          r.uomCode,
          num(r.requiredQuantity),
          num(r.availableQuantity),
          <Shortage value={r.shortageQuantity} />,
          r.requiredByTime?.slice(0, 5) ?? '-',
          <PriorityTag priority={r.priority} />,
          editable ? (
            <RowActions
              onEdit={() => setEditMaterial(r)}
              onRemove={() => m.removeMaterial.mutate(r.id)}
            />
          ) : null,
        ])}
      />

      <PlanSection
        title="Labour required tomorrow"
        onAdd={editable ? () => setDialog('labour') : undefined}
        columns={['Labours', 'Contractor', 'Required', 'Available', 'Shortage', 'Hours', '']}
        rows={plan.labour.map((r) => [
          r.labourCategoryName,
          r.contractorName ?? 'Own labour',
          r.requiredWorkers,
          r.availableWorkers,
          <Shortage value={r.shortageWorkers} />,
          num(r.workingHours),
          editable ? (
            <RowActions onEdit={() => setEditLabour(r)} onRemove={() => m.removeLabour.mutate(r.id)} />
          ) : null,
        ])}
      />

      <PlanSection
        title="Work planned tomorrow"
        onAdd={editable ? () => setDialog('work') : undefined}
        columns={['Description', 'Qty', 'Labour', 'Start', 'End', 'Priority', 'Readiness', '']}
        rows={plan.work.map((r) => [
          <Box>
            {r.description}
            {r.carriedForwardFromId && (
              <Chip label="carried forward" size="small" sx={{ ml: 1, fontSize: 11 }} />
            )}
          </Box>,
          r.plannedQuantity != null ? `${num(r.plannedQuantity)} ${r.uomCode ?? ''}` : '-',
          r.requiredLabourCount ?? '-',
          r.plannedStartTime?.slice(0, 5) ?? '-',
          r.plannedEndTime?.slice(0, 5) ?? '-',
          <PriorityTag priority={r.priority} />,
          <Chip
            size="small"
            label={r.status.replace(/_/g, ' ').toLowerCase()}
            sx={{
              fontWeight: 700,
              ...(r.blocked
                ? { bgcolor: '#fbe0de', color: '#8c1d18' }
                : r.status === 'READY'
                  ? { bgcolor: '#dcf2e6', color: '#0b5d3b' }
                  : { bgcolor: '#e6ebef', color: '#4a5a68' }),
            }}
          />,
          editable ? (
            <RowActions onEdit={() => setEditWork(r)} onRemove={() => m.removeWork.mutate(r.id)} />
          ) : null,
        ])}
      />

      <Dialog open={dialog === 'material'} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <MaterialPlanForm
          pending={m.addMaterial.isPending}
          error={m.addMaterial.error}
          onCancel={() => setDialog(null)}
          onSave={async (b) => {
            await m.addMaterial.mutateAsync(b)
            setDialog(null)
          }}
        />
      </Dialog>
      <Dialog open={dialog === 'labour'} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <LabourPlanForm
          pending={m.addLabour.isPending}
          error={m.addLabour.error}
          onCancel={() => setDialog(null)}
          onSave={async (b) => {
            await m.addLabour.mutateAsync(b)
            setDialog(null)
          }}
        />
      </Dialog>
      <Dialog open={dialog === 'work'} onClose={() => setDialog(null)} fullWidth maxWidth="md">
        <WorkPlanForm
          pending={m.addWork.isPending}
          error={m.addWork.error}
          onCancel={() => setDialog(null)}
          onSave={async (b) => {
            await m.addWork.mutateAsync(b)
            setDialog(null)
          }}
        />
      </Dialog>

      <Dialog open={Boolean(editMaterial)} onClose={() => setEditMaterial(null)} fullWidth maxWidth="sm">
        {editMaterial && (
          <MaterialPlanForm
            initial={editMaterial}
            pending={m.editMaterial.isPending}
            error={m.editMaterial.error}
            onCancel={() => setEditMaterial(null)}
            onSave={async (b) => {
              await m.editMaterial.mutateAsync({ id: editMaterial.id, body: b })
              setEditMaterial(null)
            }}
          />
        )}
      </Dialog>
      <Dialog open={Boolean(editLabour)} onClose={() => setEditLabour(null)} fullWidth maxWidth="sm">
        {editLabour && (
          <LabourPlanForm
            initial={editLabour}
            pending={m.editLabour.isPending}
            error={m.editLabour.error}
            onCancel={() => setEditLabour(null)}
            onSave={async (b) => {
              await m.editLabour.mutateAsync({ id: editLabour.id, body: b })
              setEditLabour(null)
            }}
          />
        )}
      </Dialog>
      <Dialog open={Boolean(editWork)} onClose={() => setEditWork(null)} fullWidth maxWidth="md">
        {editWork && (
          <WorkPlanForm
            initial={editWork}
            pending={m.editWork.isPending}
            error={m.editWork.error}
            onCancel={() => setEditWork(null)}
            onSave={async (b) => {
              await m.editWork.mutateAsync({ id: editWork.id, body: b })
              setEditWork(null)
            }}
          />
        )}
      </Dialog>
    </>
  )
}

// ------------------------------------------------------------------ forms

function MaterialPlanForm({ pending, error, initial, onCancel, onSave }: FormProps<MaterialPlan>) {
  const materials = useMaterials({ page: 0, size: 300 })
  const [f, setF] = useState({
    materialId: initial?.materialId ?? '',
    required: initial ? String(initial.requiredQuantity) : '',
    available: initial ? String(initial.availableQuantity) : '0',
    time: initial?.requiredByTime?.slice(0, 5) ?? '',
    priority: initial?.priority ?? 'MEDIUM',
  })
  const shortage = Math.max(Number(f.required || 0) - Number(f.available || 0), 0)

  return (
    <>
      <DialogTitle>
        {initial ? 'Edit material requirement' : 'Material required tomorrow'}
      </DialogTitle>
      <DialogContent dividers>
        {error != null && <Box sx={{ mb: 2 }}><ErrorBlock error={error} /></Box>}
        <TextField select label="Material" required fullWidth value={f.materialId}
                   onChange={(e) => setF({ ...f, materialId: e.target.value })} sx={{ mb: 2 }}>
          {(materials.data?.content ?? []).map((x) => (
            <MenuItem key={x.id} value={x.id}>{x.code} — {x.name} ({x.uomCode})</MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <TextField label="Required quantity" type="number" required value={f.required}
                     onChange={(e) => setF({ ...f, required: e.target.value })} />
          <TextField label="Available on site" type="number" value={f.available}
                     onChange={(e) => setF({ ...f, available: e.target.value })} />
          <TextField label="Required by" type="time" value={f.time}
                     onChange={(e) => setF({ ...f, time: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select label="Priority" value={f.priority}
                     onChange={(e) => setF({ ...f, priority: e.target.value as PlanPriority })}>
            {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p.toLowerCase()}</MenuItem>)}
          </TextField>
        </Box>
        <Alert severity={shortage > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
          {shortage > 0 ? `Shortage of ${shortage} — order tonight.` : 'Enough on site.'}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={pending || !f.materialId || !(Number(f.required) > 0)}
                onClick={() => onSave({
                  materialId: f.materialId,
                  requiredQuantity: Number(f.required),
                  availableQuantity: Number(f.available || 0),
                  requiredByTime: f.time ? `${f.time}:00` : undefined,
                  priority: f.priority,
                })}>
          {pending ? 'Saving...' : initial ? 'Save changes' : 'Add to plan'}
        </Button>
      </DialogActions>
    </>
  )
}

function LabourPlanForm({ pending, error, initial, onCancel, onSave }: FormProps<LabourPlan>) {
  const categories = useLabourCategories()
  const contractors = useContractors()
  const [f, setF] = useState({
    categoryId: initial?.labourCategoryId ?? '',
    contractorId: initial?.contractorId ?? '',
    required: initial ? String(initial.requiredWorkers) : '',
    available: initial ? String(initial.availableWorkers) : '0',
  })
  const shortage = Math.max(Number(f.required || 0) - Number(f.available || 0), 0)

  return (
    <>
      <DialogTitle>{initial ? 'Edit labour requirement' : 'Labour required tomorrow'}</DialogTitle>
      <DialogContent dividers>
        {error != null && <Box sx={{ mb: 2 }}><ErrorBlock error={error} /></Box>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
          <TextField select label="Labours" required value={f.categoryId}
                     onChange={(e) => setF({ ...f, categoryId: e.target.value })}
                     sx={{ gridColumn: '1 / -1' }}>
            {(categories.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select label="Contractor" value={f.contractorId}
                     onChange={(e) => setF({ ...f, contractorId: e.target.value })}
                     sx={{ gridColumn: '1 / -1' }}>
            <MenuItem value="">Own labour</MenuItem>
            {(contractors.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField label="Workers required" type="number" required value={f.required}
                     onChange={(e) => setF({ ...f, required: e.target.value })} />
          <TextField label="Contractor can field" type="number" value={f.available}
                     onChange={(e) => setF({ ...f, available: e.target.value })} />
        </Box>
        <Alert severity={shortage > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
          {shortage > 0 ? `Short by ${shortage} worker(s).` : 'Enough labour committed.'}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={pending || !f.categoryId || !(Number(f.required) > 0)}
                onClick={() => onSave({
                  labourCategoryId: f.categoryId,
                  contractorId: f.contractorId || undefined,
                  requiredWorkers: Number(f.required),
                  availableWorkers: Number(f.available || 0),
                })}>
          {pending ? 'Saving...' : initial ? 'Save changes' : 'Add to plan'}
        </Button>
      </DialogActions>
    </>
  )
}

function WorkPlanForm({ pending, error, initial, onCancel, onSave }: FormProps<WorkPlan>) {
  const [f, setF] = useState({
    description: initial?.description ?? '',
    quantity: initial?.plannedQuantity != null ? String(initial.plannedQuantity) : '',
    labour: initial?.requiredLabourCount != null ? String(initial.requiredLabourCount) : '',
    start: initial?.plannedStartTime?.slice(0, 5) ?? '',
    end: initial?.plannedEndTime?.slice(0, 5) ?? '',
    priority: initial?.priority ?? 'MEDIUM',
    status: initial?.status ?? 'PLANNED',
    remarks: initial?.remarks ?? '',
  })

  return (
    <>
      <DialogTitle>{initial ? 'Edit planned work' : 'Work planned for tomorrow'}</DialogTitle>
      <DialogContent dividers>
        {error != null && <Box sx={{ mb: 2 }}><ErrorBlock error={error} /></Box>}
        <TextField label="What will be done" required fullWidth multiline minRows={2}
                   value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                   sx={{ mb: 2 }} placeholder="Slab shuttering - 3rd floor east wing" />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
          <TextField label="Planned quantity" type="number" value={f.quantity}
                     onChange={(e) => setF({ ...f, quantity: e.target.value })} />
          <TextField label="Labour needed" type="number" value={f.labour}
                     onChange={(e) => setF({ ...f, labour: e.target.value })} />
          <TextField select label="Priority" value={f.priority}
                     onChange={(e) => setF({ ...f, priority: e.target.value as PlanPriority })}>
            {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p.toLowerCase()}</MenuItem>)}
          </TextField>
          <TextField label="Start" type="time" value={f.start}
                     onChange={(e) => setF({ ...f, start: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="End" type="time" value={f.end}
                     onChange={(e) => setF({ ...f, end: e.target.value })}
                     slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select label="Readiness" value={f.status}
                     onChange={(e) => setF({ ...f, status: e.target.value as WorkPlanStatus })}
                     helperText="Name the blocker if there is one">
            {WORK_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, ' ').toLowerCase()}</MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField label="Remarks" fullWidth multiline minRows={2} sx={{ mt: 2 }}
                   value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={pending || f.description.trim().length < 3}
                onClick={() => onSave({
                  description: f.description.trim(),
                  plannedQuantity: f.quantity ? Number(f.quantity) : undefined,
                  requiredLabourCount: f.labour ? Number(f.labour) : undefined,
                  plannedStartTime: f.start ? `${f.start}:00` : undefined,
                  plannedEndTime: f.end ? `${f.end}:00` : undefined,
                  priority: f.priority,
                  status: f.status,
                  remarks: f.remarks || undefined,
                })}>
          {pending ? 'Saving...' : initial ? 'Save changes' : 'Add to plan'}
        </Button>
      </DialogActions>
    </>
  )
}

// --------------------------------------------------------------- helpers

interface FormProps<T> {
  pending: boolean
  error: unknown
  /** Present when an existing plan row is being corrected rather than added. */
  initial?: T
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}

function PlanSection({
  title,
  columns,
  rows,
  onAdd,
}: {
  title: string
  columns: string[]
  rows: ReactNode[][]
  onAdd?: () => void
}) {
  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {onAdd && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>
            Add
          </Button>
        )}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c, i) => (
                <TableCell key={i}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((cells, r) => (
              <TableRow key={r} hover>
                {cells.map((cell, c) => (
                  <TableCell key={c}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Nothing planned yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

function Shortage({ value }: { value?: number | null }) {
  const n = Number(value ?? 0)
  if (n <= 0) return <span style={{ color: '#0b5d3b' }}>none</span>
  return <strong style={{ color: '#b3261e' }}>{num(n)}</strong>
}

function PriorityTag({ priority }: { priority: PlanPriority }) {
  const s = PRIORITY_COLOR[priority] ?? PRIORITY_COLOR.MEDIUM
  return (
    <Chip label={priority.toLowerCase()} size="small"
          sx={{ color: s.color, bgcolor: s.bg, fontWeight: 700 }} />
  )
}

function RowActions({ onEdit, onRemove }: { onEdit: () => void; onRemove: () => void }) {
  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <IconButton size="small" onClick={onEdit} title="Edit this line">
        <EditOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" color="error" onClick={onRemove} title="Remove from the plan">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

function num(value?: number | null): string {
  if (value == null) return '-'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
}
